import { action } from 'mobx';

/* eslint-disable no-use-before-define, no-fallthrough, no-duplicate-case */
import { makeWasmObservable } from 'libs/rendering/gltf/api/wasmObservable';

// import { modalStore } from 'apps/modals/app';
// import { Modal } from 'libs/rendering/gltf/components/modal';
// import { fromLocalStorage } from 'core/localStorage';

import color from 'styles/themes/wikifactory/styles/colors';
import { isMobile } from 'styles/utils';

import { PinConfig, UiModes } from 'libs/rendering/gltf/config';
import { routerStore } from 'core/routing';

import { SectionState } from 'libs/rendering/gltf/components/sectionBrowser/sectionBrowser';

export class ShaderColorOptions {
  // https://doc.instantreality.org/tools/color_calculator/
  // TOOD replace hex codes with new theme lookup after re-brand. color names
  // added in comments already - you're welcome ;)
  constructor() {
    /* FACES */
    this.FaceColorHighlighted = color.wfBlue700;
    this.FaceColorSelected = color.wfBlue400;
    this.FaceColorSelectedAndHighlighted = color.wfBlue400;
    // pin notes...
    this.FaceColorIndirectMkpHighlight = color.wfPurple;
    this.FaceColorIndirectPinHighlight = color.wfPurple;
    // dfm
    this.FaceColorDfmHighlighted = color.wfOrange;
    this.FaceColorDfmSelectedAndHighlighted = color.wfPurple;
    // navcube
    this.FaceColorNavCubeHighlight = color.wfGrey400;
    this.FaceColorNavCubeSelected = color.wfYellow;

    /* EDGES */
    this.EdgeColorSelected = color.wfBlue;
    this.EdgeColorHighlighted = color.wfPurple;
    this.EdgeColorSelectedAndHighlighted = color.wfRed;
    // edge mark on face sel/high
    this.EdgeColorCoHighlighted = color.cyan;
    this.EdgeColorCoSelected = color.wfBlue;
    this.EdgeColorCoSelectedAndHighlighted = color.cyan;
    // annotations, markup
    this.EdgeColorIndirectMkpHighlight = color.wfPink;
    this.EdgeColorIndirectPinHighlight = color.wfPink;
    // DFM
    this.EdgeColorDfmHighlight = color.wfYellow;
    this.EdgeColorDfmCoselected = color.wfPink;
    // navcube
    this.EdgeColorNavCube = color.wfGrey400;
    // aux face edges
    this.EdgeColorGhost = color.wfWhite;
  }

  get options() {
    const obj = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const prop in this) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.hasOwnProperty(prop)) {
        obj[prop] = this[prop];
      }
    }

    const optsStr = JSON.stringify(obj);
    return optsStr;
  }
}

// VIEWER
export class Cad3dViewer {
  constructor(wftyModule, viewerControl, startInDiffMode) {
    this._holder = null; // HTML wrapper <div> element
    this._canvas = null; // HTML <canvas> element

    this.takeFPSmeasurements = false; // If true, the console log reports every 100 frames the average speed...
    this._wasm = null;
    this._cadFile = null;

    this.clear();

    this.ignorePinAddition = false;

    this.viewerControl = viewerControl;

    // default status
    this._status = new wftyModule.StatusEmbind();

    if (!wftyModule) {
      this._status.error = -1;
      this._status.reason = 'invalid Wikifactory wasm module';
      this.viewerControl.LogFail(this._status.reason);
      return;
    }

    // init color options
    this.colorOptions = new ShaderColorOptions();

    try {
      // instantiate the viewer
      this._wasm = new wftyModule.Cad3dViewerEmbind(
        this.viewerControl,
        startInDiffMode,
      );
    } catch (e) {
      this.viewerControl.LogException(e);
      return;
    }

    if (this._wasm) {
      try {
        // apply colorOptions
        const optsStr = this.colorOptions.options;
        this._wasm.SetColorOptions(optsStr);

        const bckgr3D = color.wfWhite;
        const bckgr2D = color.wfGrey100;
        this._wasm.SetBackgroundColors(bckgr3D, bckgr2D);

        // lighting opions
        // NOTE: oldLighting = { ambient:0.35, diffuse: 0.75, specular: 0, shininess: 32 }
        // Phong shading used. See https://learnopengl.com/Lighting/Basic-Lighting
        const lighting = {
          ambient: 0.35,
          diffuse: 0.75,
          specular: 0.3,
          shininess: 16.0,
        };
        this.setLightingOptions(lighting);
      } catch (e) {
        this.viewerControl.LogException(e);
      }
    } else {
      this._status.error = -1;
      this._status.reason = 'cannot instantiate Cad3dViewer';
      this.viewerControl.LogFail(this._status.reason);
    }
  }

  clear = () => {
    this._notePad = null;
    this._diffTool = null;
    this._settings = null;
    this._engine = null;
    this._selectionManager = null;
    this._sectionManager = null;
    this._window = null;
    this._navCube = null;
    this._session = null;
  };

  get status() {
    if (this._wasm) {
      return this._status;
    }
    return { error: -1, reason: 'empty viewer' };
  }

  get isOk() {
    return this.status?.error === 0;
  }

  get isPreview() {
    return this.isOk ? this._wasm.isPreview : false;
  }

  set isPreview(b) {
    if (this.isOk) this._wasm.isPreview = b;
  }

  // unique part update
  get needsUniquePartUpdate() {
    try {
      return this.isOk && this._wasm.NeedsUniquePartUpdate();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return false;
  }

  autoFocusUniquePartIfNeeded = () => {
    try {
      if (this.needsUniquePartUpdate) this._wasm.AutoFocusUniquePart();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  get module() {
    return this.viewerControl ? this.viewerControl.wftyModule : null;
  }

  setLightingOptions = lighting => {
    if (lighting) {
      if (
        !Number.isNaN(lighting.ambient) &&
        !Number.isNaN(lighting.diffuse) &&
        !Number.isNaN(lighting.specular) &&
        !Number.isNaN(lighting.shininess)
      ) {
        try {
          this._wasm.SetLightingOptions(lighting);
        } catch (e) {
          this.viewerControl.LogException(e);
        }
      }
    }
  };

  get metadata() {
    return this._wasm.GetMetaData();
  }

  get settings() {
    if (!this._settings) {
      // create observable
      this._settings = makeWasmObservable(
        this.viewerControl.obsMap,
        this._wasm?.GetView(),
        this.viewerControl,
      );
    }
    return this._settings;
  }

  get engine() {
    if (!this._engine) {
      // create observable
      this._engine = makeWasmObservable(
        this.viewerControl.obsMap,
        this._wasm.GetEngine(),
        this.viewerControl,
      );
    }
    return this._engine;
  }

  get selectionManager() {
    if (!this._selectionManager) {
      // create observable
      this._selectionManager = makeWasmObservable(
        this.viewerControl.obsMap,
        this._wasm.GetSelectionManager(),
        this.viewerControl,
      );
    }
    return this._selectionManager;
  }

  get sectionManager() {
    if (!this._sectionManager) {
      // create observable
      this._sectionManager = makeWasmObservable(
        this.viewerControl.obsMap,
        this._wasm.GetSectionView(),
        this.viewerControl,
      );
    }
    return this._sectionManager;
  }

  addNewNote = () => {
    if (!this.selectionManager.IsSelectionModeLink()) return false;
    if (!this._notePad) return false;

    if (this.ignorePinAddition === true) {
      // clicking on existing pin note in pin-addition mode...
      this.ignorePinAddition = false;
      return false;
    }

    // 'link' selection, not a selection but a pin note
    const lastIx = this.selectionManager.GetSelectionCount() - 1;
    const pinTarget = this.selectionManager.GetSelection(lastIx);
    if (!pinTarget || !pinTarget.HasPoint()) {
      // nothing was selected...
      return false;
    }

    // overwrite proper target type
    const isFeature = !pinTarget.IsInstance();
    if (isFeature) {
      // pin
      pinTarget.SetIndirectPin();
    } else {
      // indirect
      pinTarget.SetIndirect();
    }

    // create the new note
    const { error, key } = this._notePad.NewNote(pinTarget._wasm);
    if (error) {
      this.viewerControl.LogException('could not create new note');
      return false;
    }
    this.viewerControl.TogglePinExpand(key);
    this._notePad.reportWasmChange();

    return true;
  };

  setBuildDirectionFromLastSelection = () => {
    if (!this.selectionManager.IsSelectionModeBuildDir()) return false;

    // get target from last selection
    const lastIx = this.selectionManager.GetSelectionCount() - 1;
    const lastTgt = this.selectionManager.GetSelection(lastIx);
    if (lastTgt && lastTgt.IsTriangle() && lastTgt.HasNormal()) {
      const nr = lastTgt.GetNr();
      this.settings.SetBuildDir({
        x: -nr.x,
        y: -nr.y,
        z: -nr.z,
      });

      // delete selection used as pin (it's a pin note, not a selection)
      this.selectionManager.DeleteLastSelection();
      this.selectionManager.ClearHighlight();
    }

    this.selectionManager.SetSelectionMode(this.module.SelectionMode);
    return false;
  };

  // window 'cursor' & events management
  get window() {
    if (!this._window && this._wasm) {
      // create observable
      this._window = makeWasmObservable(
        this.viewerControl.obsMap,
        this._wasm.GetWindow(),
        this.viewerControl,
      );
    }
    return this._window;
  }

  // NavCube position management
  get navCube() {
    if (!this._navCube) {
      // create observable
      this._navCube = makeWasmObservable(
        this.viewerControl.obsMap,
        this._wasm.GetNavCube(),
        this.viewerControl,
      );
    }
    return this._navCube;
  }

  // Ui mode (context)
  get mode() {
    try {
      return this.isOk ? this._wasm.GetUiMode() : undefined;
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return undefined;
  }

  set mode(m) {
    try {
      if (this.isOk) {
        switch (m) {
          case UiModes.assembly:
            this._wasm.SetUiMode(this.module.UiMode.UiModeAssembly);
            break;
          case UiModes.geometry:
            this._wasm.SetUiMode(this.module.UiMode.UiModeGeometry);
            break;
          case UiModes.mesh:
            this._wasm.SetUiMode(this.module.UiMode.UiModeMesh);
            break;
          case UiModes.markup:
            this._wasm.SetUiMode(this.module.UiMode.UiModeMarkup);
            break;
          case UiModes.feature:
            this._wasm.SetUiMode(this.module.UiMode.UiModeFeature);
            break;
          case UiModes.wallFeature:
            this._wasm.SetUiMode(this.module.UiMode.UiModeWallFeature);
            break;
          case UiModes.section:
            this._wasm.SetUiMode(this.module.UiMode.UiModeSection);
            break;
          case UiModes.measurements:
            this._wasm.SetUiMode(this.module.UiMode.UiModeMeasurements);
            break;
          case UiModes.collapsed:
            this._wasm.SetUiMode(this.module.UiMode.UiModeNone);
            break;
          default:
            break;
        }
      }
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  }

  // viewer state serialization
  get session() {
    if (!this._session) {
      // create observable
      this._session = makeWasmObservable(
        this.viewerControl.obsMap,
        this._wasm.GetSession(),
        this.viewerControl,
      );
    }
    return this._session;
  }

  // NotePad: 'pin' comments manager
  get notePad() {
    if (!this._notePad) {
      // create observable
      this._notePad = makeWasmObservable(
        this.viewerControl.obsMap,
        this._wasm.GetNotePad(),
        this.viewerControl,
      );
    }
    return this._notePad;
  }

  initNotePad = () => {
    try {
      if (this.isOk && this._cadFile) {
        const { obsMap } = this.viewerControl;
        this._notePad = makeWasmObservable(
          obsMap,
          this._wasm.GetNotePad(),
          this.viewerControl,
        );
      } else {
        this.viewerControl.LogException(
          '[cad3dViewer/initNotePad] ERROR, cadFile is not ok, notepad not initialized',
        );
      }
    } catch (e) {
      this.viewerControl.LogException(e.toString());
    }
  };

  // DiffTool
  get diffTool() {
    if (this.isOk) {
      if (!this._diffTool) {
        // create observable
        this._diffTool = makeWasmObservable(
          this.viewerControl.obsMap,
          this._wasm.GetDiffTool(),
          this.viewerControl,
        );
      }
      return this._diffTool;
    }
    return null;
  }

  @action stopDiffing = () => {
    this._diffTool?.StopDiffing();
    this._wasm.Draw();
  };

  @action startDiffing = () => {
    this._diffTool?.StartDiffing();
    this._wasm.Draw();
  };

  get isDiffing() {
    if (this._diffTool) {
      return this._diffTool.isWorking;
    }
    return false;
  }

  get isDiffLoading() {
    if (this._diffTool) {
      return this._diffTool.isLoading;
    }
    return false;
  }

  // CAD file to render
  @action SetCadFile = cadFile => {
    try {
      if (cadFile) {
        if (!cadFile.isOk) {
          this._status.error = -1;
          this._status.reason = 'invalid cad file';
          this.viewerControl.LogConversionProblem(this._status.reason);
        } else {
          this._status = this._wasm.SetCadFile(cadFile._wasm);
          if (this._status.error !== 0) {
            this.viewerControl.LogConversionProblem(this._status.reason);
          } else {
            this._cadFile = cadFile;
          }

          // set default camera orbit
          this._settings?.SetOrbitFixedUp();

          this.initNotePad();
        }
      } else {
        this._wasm.SetCadFile(null);
      }
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  // CAD file to diff
  @action SetDiffCadFile = cadFile => {
    try {
      if (cadFile) {
        if (!cadFile.isOk) {
          this._status.error = -1;
          this._status.reason = 'invalid diff cad file';
          this.viewerControl.LogConversionProblem(this._status.reason);
        } else {
          this._wasm.SetDiffCadFile(cadFile._wasm);
        }
      } else {
        this._wasm.SetDiffCadFile(null);
      }
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  @action RestoreDiffCadFile = id => {
    try {
      return this._wasm.RestoreDiffCadFile(id);
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return null;
  };

  get isRendering() {
    try {
      return this.isOk ? this._wasm.IsRendering() : false;
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return false;
  }

  // - set the canvas, this can also be used to set the canvas back
  // to nothing, by passing (null, null)
  setCanvas = (holderEl, canvasEl) => {
    if (holderEl && canvasEl) {
      this._holder = holderEl; // HTML wrapper <div> element
      this._canvas = canvasEl; // HTML <canvas> element
      this._wasm.SetCanvas(holderEl.id, canvasEl.id);
      try {
        return this._wasm.IsCanvasSet();
      } catch (e) {
        this.viewerControl.LogException(e);
      }
      return false;
    }

    try {
      this._wasm.SetCanvas('', '');
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return false;
  };

  // - this sets a flag in wasm which will run code to get the size of the
  // canvas wrapper element and set that size in the wasm window instance
  // - then it will send a UserAction notification back to the js to notify
  // us about a changed canvas, so don't call this in the canvas3D render
  // function
  // - this is responsible for the UpdateCanvasSize: Xpx Ypx messages
  updateCanvas = () => {
    this.window?.UpdateCanvas();
  };

  loopCondition = () => {
    if (
      this.viewerControl?.conversionProblem ||
      this.viewerControl?.viewerFailed
    ) {
      return false;
    }

    if (!this._wasm) {
      return false;
    }

    try {
      return this._wasm.LoopCondition();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
    return false;
  };

  renderFrame = () => {
    // - early return to prevent error logs about 'null is not an object'
    // when trying to call RenderFrame after viewer was stopped
    if (!this._wasm) {
      return;
    }

    // - early return when viewer is stopped to now render extra frame
    if (!this.loopCondition()) {
      return;
    }

    try {
      this._wasm.RenderFrame(this.takeFPSmeasurements);
    } catch (e) {
      // - we don't return here after logException because we allow one
      // exception to happen before stopping the renderLoop, that is because
      // we won't to avoid failing the viewer for minor problems that end up
      // being reported to sentry as exception, like for example problems in
      // the wasmObservables code
      this.viewerControl.LogException(e);
    }

    // - once we're here then loopCondition is true and this._wasm is defined,
    // - an exception could have happened, but if there is a serious problem then
    // the next renderFrame call should return early if the viewer is destroyed
    // on if (!this._wasm), or viewerFailed == true on if (!this.loopCondition())
    window.requestAnimationFrame(this.renderFrame);
  };

  // starts rendering
  startRendering = () => {
    if (this.isOk) {
      this.start();
      window.requestAnimationFrame(this.renderFrame);
    }
  };

  start = () => {
    try {
      if (this.isOk) this._wasm.Start();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  stop = () => {
    try {
      if (this.isOk) this._wasm.Stop();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  // clear the canvas with a fixed white color, added so I can try working
  // around black flickering by calling this
  clearCanvas = () => {
    try {
      if (this.isOk) this._wasm.ClearCanvas();
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  // reloads rendering assets (GPU state)
  reload = () => {
    try {
      if (this.isOk) {
        const status = this._wasm.Reload();
        return status.error;
      }
    } catch (e) {
      this.viewerControl.LogException(e);
    }

    return false;
  };

  // loads data for newly created tab
  loadFocusedIntem = () => {
    try {
      if (this.isOk) {
        this._wasm.LoadFocusedItem();
      }
    } catch (e) {
      this.viewerControl.LogException(e);
    }
  };

  // free (GPU & CPU) memory used by the Cad3dViewer
  destroy = () => {
    try {
      this._wasm.Destroy();
      this._wasm.delete();
    } catch (e) {
      this.viewerControl?.logWarn(e);
    }

    this.viewerControl.canvasSet = false;
    this._wasm = null;
  };

  /*
  ::::::::::::::::::::::::::::::::::::::::::::::::::::
                'Reverse' callback API
                ----------------------
    c++ calls below JS function(s) when events occur
          so it notifies the JS side about it.
  ::::::::::::::::::::::::::::::::::::::::::::::::::::
  */

  handleUserActionWithTarget = (targetJSON, actionType) => {
    if (!this.isOk) return;
    const actionEnum = this.module.UserActionType;
    if (targetJSON) {
      // here the UI updates stuff given the 'affected' cadItem ...
      switch (actionType) {
        case actionEnum.UserActionHighlight.value:
          this.viewerControl.SelectionUpdated(true);
          break;
        case actionEnum.UserActionSelection.value:
          this.viewerControl.SelectionUpdated(true);
          // pass the target for pin note addition...
          this.addNewNote();
          // pass the target for build dir setting...
          this.setBuildDirectionFromLastSelection();
          break;
        case actionEnum.UserActionUnSelection.value:
          this.viewerControl.SelectionUpdated(true);
          break;
        case actionEnum.UserActionFocusLoaded.value:
          // focus set
          this.viewerControl.CameraUpdated(true);
          break;
        default:
          this.viewerControl.logWarn(
            `UNKNOWN USER ACTION ${actionType} ${targetJSON}`,
          );
          break;
      }
    }
  };

  handleUserAction = (value, actionType) => {
    if (!this.isOk) return;
    const actionEnum = this.module.UserActionType;

    // here the UI updates stuff...
    switch (actionType) {
      // UNSELECTION
      case actionEnum.UserActionUnSelection.value:
        this.viewerControl.SelectionUpdated(true);
        break;
      case actionEnum.UserActionInspectTabCreated.value:
        // tab created, start brep/mkp json parse...
        this.loadFocusedIntem();
        break;
      case actionEnum.UserActionInspectLoaded.value:
        // tab finished data loading or tab closed (value === -1)...
        this.viewerControl.ViewUpdated(true);
        break;
      /* SELECTION MODE */
      case actionEnum.UserActionSelectionMode.value:
        // selection mode changed...
        // TODO
        break;
      case actionEnum.UserActionStandardView.value:
        this.viewerControl.CameraUpdated(false);
        break;
      case actionEnum.UserActionTessFieldProgress.value:
        this.viewerControl.CameraUpdated(false);
        break;
      case actionEnum.UserActionWarningsProgress.value:
        this.viewerControl.WarningsUpdated(false);
        break;
      default:
        this.viewerControl.logWarn(`unknown user action ${actionType}`);
        break;
    }
  };

  handleUserFlaggedAction = (value, flag, actionType) => {
    if (!this.isOk) return;
    const actionEnum = this.module.UserActionType;

    const newpath = routerStore.route.name;
    const camera = this.viewerControl.viewer.settings.CurrentCamera();
    const focusedInstanceIx = camera?.GetInstanceIndex();

    const { assembly } = this.viewerControl.cadFile;
    const focusedInstance = assembly.GetInstance(focusedInstanceIx);

    switch (actionType) {
      /* DBL CLICK TAB CREATION */
      case actionEnum.UserActionInspect.value:
        this.viewerControl.viewer?.settings?.reportWasmChange();
        // Update the URL
        if (focusedInstance) {
          routerStore.navigate(newpath, {
            ...routerStore.route.params,
            partId: focusedInstance.GetBody().uuid,
          });
        }
        this.viewerControl.ViewUpdated(false);
        break;
      default:
        break;
    }
  };

  handleUserEvent = actionType => {
    if (!this.isOk) return;
    const actionEnum = this.module.UserActionType;

    // here the UI updates stuff...
    switch (actionType) {
      case actionEnum.UserActionClearSelection.value:
      case actionEnum.UserActionUnHighlight.value:
        // clear all highlight...
        this.viewerControl.SelectionUpdated(true);
        this.viewerControl.WarningsUpdated(false);
        break;
      case actionEnum.UserActionUserCameraUnHighlight.value:
      case actionEnum.UserActionUserCameraHighlight.value:
        // user camera highlight change
        this.session?.reportWasmChange();
        break;
      case actionEnum.UserActionCadAssemblyReady.value:
        // assembly ready...
        this.viewerControl.SetAssemblyReady(true);
        break;
      case actionEnum.UserActionCadRequestFinished.value:
        if (this.viewerControl.cadFile && !this.isDiffing) {
          this.handleEventCadRequestFinished();
        }
        this.viewerControl.ViewUpdated(false);
        break;
      case actionEnum.UserActionCameraMVP.value:
        // camera view-projection matrices changed...
        this.viewerControl.CameraUpdated(true);
        break;
      case actionEnum.UserActionFocusLoaded.value:
        // focus unset (unfocus)
        this.viewerControl.CameraUpdated(true);
        break;
      // PATCH MANAGER
      case actionEnum.UserActionPatchManagerUpdate.value:
        // pin notes...
        break;
      case actionEnum.UserActionTessFieldFinished.value:
        // mesh analysis finished
        this.viewerControl.CameraUpdated(false);
        // start warnings calculation
        this.viewerControl.StartWarningsCalculation();
        break;
      case actionEnum.UserActionTessFieldWarnings.value:
        this.viewerControl.WarningsUpdated(false);
        break;
      case actionEnum.UserActionCanvasSize.value:
        // canvas (window) size change
        this.window?.reportWasmChange();
        this.viewerControl.ViewUpdated(false);
        break;
      case actionEnum.UserActionEventsReady.value:
        // camera and view are ready to accept events
        this.window?.reportWasmChange();
        this.viewerControl.eventsReady = true;
        break;
      // section
      case actionEnum.UserActionClipPlaneSet.value:
        this.viewerControl.SectionViewUpdated();
        break;
      // diffing
      case actionEnum.UserActionDiffToolResult.value:
        this.viewerControl.ViewUpdated(false);
        break;
      // measurements
      case actionEnum.UserActionPmiUpdate.value:
        this.viewerControl.cadFile?.pmi?.reportWasmChange();
        this.notePad?.reportWasmChange();
        this.viewerControl.CameraUpdated(false);
        break;
      default:
        break;
    }
  };

  handleEventCadRequestFinished = () => {
    if (!this.viewerControl.cadFile) return;
    // main CAD file ready...
    this.viewerControl.cadReady = true;
    this.viewerControl.viewerVisible = true;
    this.viewerControl.SyncSelectionMode();

    // hide/show nav cube
    if (this.settings.CurrentCamera()) {
      if (
        this.settings.CurrentCamera().Is3D() &&
        !this.settings.CurrentCamera().IsPlaneXY() &&
        !this.viewerControl.isPreview
      ) {
        this.settings.SetNavCubeVisible(!isMobile());
      } else {
        this.settings.SetNavCubeVisible(false);
      }

      this.sectionState = new SectionState(
        this.sectionManager,
        this.engine,
        this.settings,
      );

      // cad-ready-promise for previews
      this.viewerControl.___sceneReadyPromiseResolve();
      // this.viewerControl.updateLightModal();
    }
  };
}
